import { ContactCompanyType } from 'models/contact_companies'
import { ContactCompanySearchResult } from 'models/companies'
import { Preferences } from 'models/preferences'
import { usePreferences } from 'context/preferences/PreferencesContext'

/**
 * Gets the URL for a First Bite company using the 2-letter type code
 *
 * @param company ContactCompanySearchResult object
 * @param preferences User preferences containing company type prefix mapping
 * @returns URL for the company
 */
export const getFirstBiteCompanyUrl = (
  company: ContactCompanySearchResult,
  preferences?: Preferences
): string => {
  if (!company || !preferences?.company_type_prefix_map || !company.type)
    return '#'

  const { contact_company_id, type } = company

  // Map 2-letter type codes to routes and company types
  // COMPANY_TYPE_UPDATE
  const typeCodeToRouteMap: Record<
    string,
    { route: string; companyType: ContactCompanyType }
  > = {
    RE: { route: 'chain', companyType: ContactCompanyType.Chain },
    HO: { route: 'hospital', companyType: ContactCompanyType.Hospital },
    DI: { route: 'k12', companyType: ContactCompanyType.K12District },
    CU: { route: 'university', companyType: ContactCompanyType.University },
    NH: { route: 'nursinghome', companyType: ContactCompanyType.NursingHome },
    LG: { route: 'lodging/chain', companyType: ContactCompanyType.Lodging },
    CS: {
      route: 'convenience/chain',
      companyType: ContactCompanyType.Convenience,
    },
  }

  const routeInfo = typeCodeToRouteMap[type]
  if (!routeInfo) return '#'

  const prefix =
    preferences.company_type_prefix_map[routeInfo.companyType] || 'RE'
  return `/${routeInfo.route}/${prefix}${contact_company_id}`
}

/**
 * Gets the URL for an unmatched company - always uses UG prefix
 *
 * @param company ContactCompanySearchResult object
 * @param preferences User preferences containing company type prefix mapping
 * @returns URL for the company
 */
export const getUnmatchedCompanyUrl = (
  company: ContactCompanySearchResult,
  preferences?: Preferences
): string => {
  if (!company || !preferences?.company_type_prefix_map) return '#'

  const { contact_company_id } = company

  // All unmatched companies use the user-generated pattern
  const ugPrefix =
    preferences.company_type_prefix_map[ContactCompanyType.UserGenerated] ||
    'UG'
  return `/company/${ugPrefix}${contact_company_id}`
}

/**
 * Gets the URL for a door (individual location)
 *
 * @param door Door search result object
 * @param isUnmatched Whether this is an unmatched door (defaults to false)
 * @param preferences User preferences containing company type prefix mapping
 * @returns URL for the door
 */
export const getDoorUrl = (
  door: any,
  isUnmatched: boolean = false,
  preferences?: Preferences
): string => {
  if (!door) return '#'

  const { type, item_id, id, chain_id } = door

  // Unmatched doors always use the UG prefix pattern
  if (isUnmatched) {
    return `/door/UG${item_id}`
  }

  // For HO, DI, CU, NH - link to chain instead of door
  // COMPANY_TYPE_UPDATE
  if (
    (type === 'HO' || type === 'DI' || type === 'CU' || type === 'NH') &&
    chain_id &&
    preferences?.company_type_prefix_map
  ) {
    const typeCodeToRouteMap: Record<
      string,
      { route: string; companyType: ContactCompanyType }
    > = {
      HO: { route: 'hospital', companyType: ContactCompanyType.Hospital },
      DI: { route: 'k12', companyType: ContactCompanyType.K12District },
      CU: { route: 'university', companyType: ContactCompanyType.University },
      NH: { route: 'nursinghome', companyType: ContactCompanyType.NursingHome },
    }

    const routeInfo = typeCodeToRouteMap[type]
    if (routeInfo) {
      const prefix =
        preferences.company_type_prefix_map[routeInfo.companyType] || 'RE'
      return `/${routeInfo.route}/${prefix}${chain_id}`
    }
  }

  // Map door types to routes for regular doors
  // RE (Restaurant) doors use restaurant route
  // COMPANY_TYPE_UPDATE
  if (type === 'RE') {
    return `/restaurant/${item_id}`
  }
  // LG (Lodging) doors use lodging door route
  else if (type === 'LG') {
    return `/lodging/door/${id}`
  }
  // CS (Convenience) doors use convenience door route
  else if (type === 'CS') {
    return `/convenience/door/${id}`
  }

  return '#'
}

/**
 * Hook version that can be used in components
 */
export const useUnifiedSearchCompanyUrl = () => {
  const { preferences } = usePreferences()

  return {
    getFirstBiteUrl: (company: ContactCompanySearchResult) =>
      getFirstBiteCompanyUrl(company, preferences),
    getUnmatchedUrl: (company: ContactCompanySearchResult) =>
      getUnmatchedCompanyUrl(company, preferences),
    getDoorUrl: (door: any, isUnmatched?: boolean) =>
      getDoorUrl(door, isUnmatched, preferences),
  }
}
